import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { App } from "./App";
import { initSentry, captureException } from "@services/config/sentry";
import { trackError } from "@services/analytics/analytics";
import { initMetaPixel } from "@services/analytics/metaPixel";
import "./index.css";

// Sentry must initialize before React renders to capture all errors
initSentry();

// No-op unless VITE_META_PIXEL_ID is set, so local runs stay out of the production pixel.
initMetaPixel();

// ── Global error capture ──
// Catches JS errors not handled by try/catch or Error Boundaries (e.g. script load failures)
window.onerror = (_message, _source, _line, _col, error) => {
  captureException(error ?? new Error(String(_message)), { component: "global" });
  trackError({
    description: error?.message ?? String(_message),
    component: "global",
    fatal: true,
  });
};

// Catches unhandled Promise rejections (failed API calls, async functions without .catch)
window.onunhandledrejection = (event: PromiseRejectionEvent) => {
  captureException(event.reason, { component: "unhandled_promise" });
  const message =
    event.reason instanceof Error
      ? event.reason.message
      : String(event.reason);
  trackError({ description: message, component: "unhandled_promise", fatal: false });
};

const rootElement = document.getElementById("root");

if (!rootElement) {
  throw new Error(
    'Root element not found. Ensure index.html contains <div id="root"></div>.',
  );
}

createRoot(rootElement).render(
  <StrictMode>
    <App />
  </StrictMode>,
);
